import { z } from 'zod';

import {
  DateAvailability,
  ScheduledMeeting,
  scheduledMeetingSchema,
} from './types';

import {
  paginatedParamsSchema,
  PaginatedResponse,
  parsePaginatedResponse,
  restAPI,
} from '../rest-api';

import { stripUndefined } from '~/utils/helpers';

type ValueOf<T> = T[keyof T];

export const MEETING_TYPES = {
  1: 'VOICE',
  2: 'VIDEO',
  3: 'IN_PERSON',
} as const;

const getMeetingTypeId = (name: ValueOf<typeof MEETING_TYPES>) => {
  return Object.entries(MEETING_TYPES).find(([, value]) => name === value)?.[0];
};

type InPersonMeetingPayload = {
  date_and_time: string;
  type: (typeof MEETING_TYPES)[3];
  report: string;
  address: string;
  room: string;
  instructions?: string;
};

type PreScheduledMeetingPayload = {
  date_and_time: string;
  type: (typeof MEETING_TYPES)[1] | (typeof MEETING_TYPES)[2];
  report: string;
  guest_attendees: string[];
  is_real_time_meeting?: true;
};

type ScheduleMeetingPayload =
  | InPersonMeetingPayload
  | PreScheduledMeetingPayload;

/* eslint-disable @typescript-eslint/no-explicit-any */
const stripNull = (obj: { [key: string]: any }): { [key: string]: any } => {
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    if (obj[key] !== null) {
      result[key] = obj[key];
    }
  }
  return result;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const toSearchParams = (
  rawParams: Record<string, any>,
  options?: {
    stripNull?: boolean;
  },
) => {
  const { stripNull: shouldStripNull = true } = options ?? {};
  const paramArg = rawParams;
  let parsed = paramArg ?? {};
  parsed = stripUndefined(paramArg);
  if (shouldStripNull) {
    parsed = stripNull(parsed);
  }

  return new URLSearchParams(parsed);
};

const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * TODO: Add pagination parms
     */
    userMeetings: builder.query<
      PaginatedResponse<ScheduledMeeting>,
      { params?: z.infer<typeof paginatedParamsSchema> } | void
    >({
      query: (args) => {
        const params = toSearchParams(args && args.params ? args.params : {}, {
          stripNull: true,
        });
        return {
          url: '/schedule/meetings',
          params: Object.fromEntries(params.entries()),
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Schedules', id: 'user-schedules' }] : [],
      transformResponse: (res) =>
        parsePaginatedResponse(scheduledMeetingSchema, res),
    }),
    reportSchedules: builder.query<
      PaginatedResponse<ScheduledMeeting>,
      { reportId: string }
    >({
      query: ({ reportId }) => ({
        url: `/schedule/meetings?report=${reportId}`,
      }),
      providesTags: (res, _err, args) =>
        res
          ? [{ type: 'Schedules', id: `report-${args.reportId}:schedules` }]
          : [],
      transformResponse: (res) =>
        parsePaginatedResponse(scheduledMeetingSchema, res),
    }),
    scheduleMeeting: builder.mutation<ScheduledMeeting, ScheduleMeetingPayload>(
      {
        query: ({ type, ...payload }) => ({
          url: '/schedule/meetings',
          method: 'POST',
          data: stripUndefined({
            ...payload,
            type: getMeetingTypeId(type) ?? 0,
          }),
        }),
        invalidatesTags: (res, _err, payload) =>
          res
            ? [{ type: 'Schedules', id: `report-${payload.report}:schedules` }]
            : [],
      },
    ),
    tenantAvailability: builder.query<
      Array<string>,
      { month: number; year: string; tenantSlug: string }
    >({
      query: (arg) => {
        return {
          url: `/schedule/availability/tenant/${arg.tenantSlug}/calendar`,
          params: {
            month: arg.month,
            year: arg.year,
          },
          withCredentials: false,
        };
      },
    }),
    dateAvailability: builder.query<
      DateAvailability,
      { date: string; tenantSlug: string }
    >({
      query: (arg) => ({
        url: `/schedule/availability/tenant/${arg.tenantSlug}/calendar/${arg.date}`,
      }),
      transformResponse: (res) => {
        if (Array.isArray(res) && res.length) {
          return res[0];
        }
        throw new Error('Invalid response');
      },
    }),
  }),
  overrideExisting: false,
});

export const ScheduleService = endpoints;
